import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Activity,
  Database,
  FileText,
  Users,
  Zap,
  TrendingUp,
  AlertCircle,
  CheckCircle,
} from "lucide-react";

interface MonitoringData {
  status: string;
  timestamp: string;
  region: string;
  performance: {
    queryTimeMs: number;
  };
  database: {
    connected: boolean;
    region: string;
  };
  metrics: {
    documents: {
      total: number;
      byFolder: Array<{ folder: string; count: string }>;
    };
    intelligenceSessions: {
      total: number;
      byStatus: Array<{ status: string; count: string }>;
    };
    transactions: {
      total: number;
      activity: {
        total: string;
        last_7_days: string;
        last_24_hours: string;
      };
    };
    vendors: {
      total: number;
      byType: Array<{ vendorType: string; count: string }>;
    };
    extractedEntities: {
      total: number;
      breakdown: Array<{ entityType: string; status: string; count: string }>;
    };
  };
}

export default function MonitoringDashboard() {
  const [data, setData] = useState<MonitoringData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const fetchMonitoring = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        "https://api-d7m4isjkga-oa.a.run.app/monitoring"
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const json = await response.json();
      setData(json);
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch monitoring data"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonitoring();
    const interval = setInterval(fetchMonitoring, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "healthy":
        return "bg-[var(--success)]";
      case "ok":
        return "bg-[var(--success)]";
      case "error":
        return "bg-destructive";
      default:
        return "bg-[var(--warning)]";
    }
  };

  const getPerformanceColor = (ms: number) => {
    if (ms < 100) return "text-[var(--success)]";
    if (ms < 500) return "text-[var(--warning)]";
    return "text-destructive";
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">System Monitoring</h1>
          <p className="text-muted-foreground mt-2">
            Real-time system health and performance metrics
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Status Overview */}
        {data && (
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  System Status
                </CardTitle>
                {data.status === "healthy" ? (
                  <CheckCircle className="h-4 w-4 text-[var(--success)]" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-destructive" />
                )}
              </CardHeader>
              <CardContent>
                <Badge className={getStatusColor(data.status)}>
                  {data.status.toUpperCase()}
                </Badge>
                <p className="text-xs text-muted-foreground mt-2">
                  Region: {data.region}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Database</CardTitle>
                <Database className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <Badge
                  className={
                    data.database.connected
                      ? "bg-[var(--success)]"
                      : "bg-destructive"
                  }
                >
                  {data.database.connected ? "CONNECTED" : "DISCONNECTED"}
                </Badge>
                <p className="text-xs text-muted-foreground mt-2">
                  {data.database.region}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Performance
                </CardTitle>
                <Zap className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div
                  className={`text-2xl font-bold ${getPerformanceColor(data.performance.queryTimeMs)}`}
                >
                  {data.performance.queryTimeMs}ms
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Query response time
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Last Update
                </CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-sm font-medium">
                  {lastUpdate.toLocaleTimeString()}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Auto-refresh: 30s
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Metrics Grid */}
        {data && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Documents */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Documents
                </CardTitle>
                <CardDescription>Document storage metrics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-3xl font-bold">
                    {data.metrics.documents.total.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Total documents
                  </p>
                </div>
                <div className="space-y-2">
                  {data.metrics.documents.byFolder.map(folder => (
                    <div
                      key={folder.folder}
                      className="flex justify-between text-sm"
                    >
                      <span className="capitalize">{folder.folder}</span>
                      <span className="font-medium">{folder.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Intelligence Sessions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Intelligence Sessions
                </CardTitle>
                <CardDescription>Batch analysis sessions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-3xl font-bold">
                    {data.metrics.intelligenceSessions.total.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Total sessions
                  </p>
                </div>
                <div className="space-y-2">
                  {data.metrics.intelligenceSessions.byStatus.map(session => (
                    <div
                      key={session.status}
                      className="flex justify-between text-sm"
                    >
                      <span className="capitalize">
                        {session.status.replace(/_/g, " ")}
                      </span>
                      <span className="font-medium">{session.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Transactions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Transactions
                </CardTitle>
                <CardDescription>Transaction activity</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-3xl font-bold">
                    {data.metrics.transactions.total.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Total transactions
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Last 24 hours</span>
                    <span className="font-medium">
                      {data.metrics.transactions.activity.last_24_hours || 0}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Last 7 days</span>
                    <span className="font-medium">
                      {data.metrics.transactions.activity.last_7_days || 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vendors */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Vendors
                </CardTitle>
                <CardDescription>Vendor database</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-3xl font-bold">
                    {data.metrics.vendors.total.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground">Total vendors</p>
                </div>
                <div className="space-y-2">
                  {data.metrics.vendors.byType.slice(0, 5).map(vendor => (
                    <div
                      key={vendor.vendorType}
                      className="flex justify-between text-sm"
                    >
                      <span className="capitalize">
                        {vendor.vendorType.replace(/_/g, " ")}
                      </span>
                      <span className="font-medium">{vendor.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Extracted Entities */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Extracted Entities
                </CardTitle>
                <CardDescription>
                  Intelligence-extracted entities from documents
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-3xl font-bold">
                    {data.metrics.extractedEntities.total.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Total extracted entities
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {data.metrics.extractedEntities.breakdown.map(
                    (entity, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="capitalize">
                          {entity.entityType} ({entity.status})
                        </span>
                        <span className="font-medium">{entity.count}</span>
                      </div>
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Loading State */}
        {loading && !data && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center space-y-4">
              <Activity className="h-12 w-12 animate-spin mx-auto text-muted-foreground" />
              <p className="text-muted-foreground">
                Loading monitoring data...
              </p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
